import {
    UpdateAppSettingsAction,
    updateAppSettings,
} from 'app/store/appSettings/actions';
import {
    mapErrorResponse,
    mapToLayoutFromUIConfig,
} from 'layout/layoutRequestMapper';
import LayoutShape from 'layout/LayoutShape';
import { buildLoggerForStore } from 'logging/writers/utils';
import { showWarningAsync } from 'notification/store/thunks';
import { portfolioSetActiveInputActionAsync } from 'portfolio/store/thunks';
import { AnyAction } from 'redux';
import Services from 'store/Services';
import State from 'store/State';
import { ThunkDispatch } from 'store/store';
import { updateWidgetConfigsFromUIConfig as updateWidgetConfigs } from 'widgets/store/thunks';
import { PortfolioSetActiveInputAction } from 'portfolio/store/actions';
import {
    LayoutActionStateChange,
    layoutStateLoadErrorAction,
    layoutStateLoadPendingAction,
    layoutStateLoadSuccessAction,
    layoutStateUpdateAction,
} from './actions';
import {
    selectActiveInputWidget,
    selectLayout,
    selectLayoutPanelId,
} from './selectors';
import {
    createWidgetLoadSharedConfigsAction,
    WidgetLoadSharedConfigsAction,
} from 'widgets/store/sharedConfiguration/actions';
import { RequestStateError } from 'communication/RequestState';
import upgradeLayout from 'layout/upgrade/upgradeLayout';
import { LayoutVersions } from 'layout/LayoutState';
import {
    createResetWidgetInstancesState,
    ResetWidgetInstancesState,
} from 'widgets/store/actions';

const log = buildLoggerForStore('layout');

export const loadLayoutActionAsync =
    (origin: string, layoutName: string) =>
    async (
        dispatch: ThunkDispatch<
            LayoutActionStateChange | UpdateAppSettingsAction
        >,
        getState: () => State,
        services: Services
    ) => {
        await loadOrSwitchLayoutActionAsync(
            dispatch,
            getState,
            services,
            origin,
            layoutName,
            false
        );
    };

export const switchLayoutAsync =
    (origin: string, layoutName: string) =>
    async (
        dispatch: ThunkDispatch<
            LayoutActionStateChange | UpdateAppSettingsAction
        >,
        getState: () => State,
        services: Services
    ) => {
        await loadOrSwitchLayoutActionAsync(
            dispatch,
            getState,
            services,
            origin,
            layoutName,
            true
        );
    };

export const layoutSetActiveInputAsPortfolioInput =
    () =>
    async (
        dispatch: ThunkDispatch<PortfolioSetActiveInputAction>,
        getState: () => State,
        { loggingService }: Services
    ) => {
        log(loggingService, 'layoutSetActiveInputAsPortfolioInput');

        const activeInput = selectActiveInputWidget(getState());
        if (!activeInput) {
            dispatch(portfolioSetActiveInputActionAsync('None'));
            return;
        }
        dispatch(
            portfolioSetActiveInputActionAsync('Widget', activeInput.component)
        );
    };

export const saveLayoutShape =
    (shape: LayoutShape | undefined) =>
    async (
        dispatch: ThunkDispatch<LayoutActionStateChange>,
        getState: () => State,
        { loggingService }: Services
    ) => {
        log(loggingService, 'saveLayoutShape', {
            shape,
        });
        const layout = selectLayout(getState());

        if (!layout) {
            return;
        }

        const nextLayout = {
            ...layout,
            shape,
        };
        dispatch(layoutStateUpdateAction(nextLayout));
    };

export type UpdateFromLayout<T extends AnyAction> = {
    [P in keyof T]: T[P];
} & { updatedFromLayout: boolean };

export function updatingFromLayout<T extends AnyAction>(
    action: T
): UpdateFromLayout<T> {
    return {
        ...action,
        updatedFromLayout: true,
    };
}

export function wasUpdatedFromLayout<T extends AnyAction>(action: T): boolean {
    return (action as UpdateFromLayout<T>).updatedFromLayout;
}

async function loadOrSwitchLayoutActionAsync(
    dispatch: ThunkDispatch<
        | LayoutActionStateChange
        | UpdateAppSettingsAction
        | WidgetLoadSharedConfigsAction
        | ResetWidgetInstancesState
    >,
    getState: () => State,
    services: Services,
    origin: string,
    layoutName: string,
    switching: boolean
) {
    const { layoutService, loggingService } = services;
    const panelId = selectLayoutPanelId(getState());

    log(loggingService, 'loadLayoutAction', {
        origin,
        layoutName,
        panelId,
        switching,
    });

    dispatch(layoutStateLoadPendingAction(layoutName, panelId));

    try {
        const uiConfig = switching
            ? await layoutService.switchLayout(layoutName, origin, panelId)
            : await layoutService.loadLayout(layoutName, origin, panelId);

        const { appSettings, components, sharedWidgetConfigs } =
            uiConfig.layout;
        const layout = mapToLayoutFromUIConfig(uiConfig);

        const version = layout.version ?? -1;
        if (version < LayoutVersions.length) {
            upgradeLayout({
                loggingService,
                dispatch,
                appSettings,
                components,
                sharedWidgetConfigs,
                layout,
            });
        }

        dispatch(updatingFromLayout(updateAppSettings(appSettings)));
        dispatch(createResetWidgetInstancesState());
        dispatch(updateWidgetConfigs(components));
        dispatch(createWidgetLoadSharedConfigsAction(sharedWidgetConfigs));

        dispatch(layoutStateLoadSuccessAction(layout));
    } catch (err) {
        const layoutError = mapErrorResponse(
            layoutName,
            origin,
            err as RequestStateError
        );
        loggingService.warn('loadLayoutAction', 'Error loading layout', {
            origin,
            layoutName,
            layoutError,
        });
        dispatch(layoutStateLoadErrorAction(layoutError));
        dispatch(showWarningAsync(`Error loading ${layoutName} layout`));
    }
}
